import { useCallback } from 'react';
import type { RefObject } from 'react';

export interface UseMenuKeyboardNavigationOptions {
  /** Ref to the menu container element */
  menuRef: RefObject<HTMLElement | null>;
  /** CSS selector for menu items (defaults to '[role="menuitem"]:not([disabled])') */
  itemSelector?: string;
  /** Callback when Escape is pressed */
  onEscape?: () => void;
}

export interface UseMenuKeyboardNavigationReturn {
  /** KeyDown handler to attach to the menu container */
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

const DEFAULT_ITEM_SELECTOR = '[role="menuitem"]:not([disabled])';

/**
 * Custom hook that handles keyboard navigation within a menu.
 *
 * Supports:
 * - ArrowDown: Move focus to the next item (wraps around)
 * - ArrowUp: Move focus to the previous item (wraps around)
 * - Home: Move focus to the first item
 * - End: Move focus to the last item
 * - Escape: Call onEscape callback (typically to close the menu)
 *
 * Includes a guard against empty items array to prevent errors when
 * menu has no enabled items.
 *
 * @example
 * ```tsx
 * const menuRef = useRef<HTMLDivElement>(null);
 * const { handleKeyDown } = useMenuKeyboardNavigation({
 *   menuRef,
 *   onEscape: () => setOpen(false),
 * });
 *
 * return <div ref={menuRef} onKeyDown={handleKeyDown}>...</div>;
 * ```
 */
const useMenuKeyboardNavigation = ({
  menuRef,
  itemSelector = DEFAULT_ITEM_SELECTOR,
  onEscape,
}: UseMenuKeyboardNavigationOptions): UseMenuKeyboardNavigationReturn => {
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
    const menu = menuRef.current;
    if (!menu) return;

    const items = Array.from(menu.querySelectorAll<HTMLElement>(itemSelector));

    // Guard: exit early if no items to navigate
    if (items.length === 0) {
      // Still handle Escape even with no items
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscape?.();
      }
      return;
    }

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const hasFocusedItem = currentIndex !== -1;
    const lastIndex = items.length - 1;

    const focusAt = (index: number): void => items[index]?.focus();

    const keyHandlers: Record<string, () => void> = {
      ArrowDown: () => focusAt(hasFocusedItem ? (currentIndex + 1) % items.length : 0),
      ArrowUp: () => focusAt(hasFocusedItem ? (currentIndex + lastIndex) % items.length : lastIndex),
      Home: () => focusAt(0),
      End: () => focusAt(lastIndex),
      Escape: () => onEscape?.(),
    };

    const handler = keyHandlers[e.key];
    if (handler) {
      e.preventDefault();
      handler();
    }
  }, [menuRef, itemSelector, onEscape]);

  return { handleKeyDown };
};

export default useMenuKeyboardNavigation;
